import { RateDto } from "arps_wasm";
import { ChartTypeLabels } from "constants/chart.constants";
import { getChartTypeAlias } from "utils/chart";
import { OptionSeries, ProductRates } from "wasm/arps";

import { IProductType } from "models/chart";

import {
  getYAxisUnit,
  scaleDataBasedOnYAxisUnits
} from "components/arps/utils/scaleDataBasedOnYAxisUnits";
import {
  McfToBcf,
  bblMcfToMbblMmcf
} from "components/multiphase-chart/constants/shared.constants";
import { IChartPreset } from "components/multiphase-chart/models/shared.models";

const VOLUME_UNITS = new Set([
  "bbl",
  "mbbl",
  "mmbbl",
  "mcf",
  "mmcf",
  "bcf",
  "boe",
  "mboe",
  "mmboe"
]);

// chart-svc scales a Rate Date / Cum Time axis to fit every product sharing it, so a volume
// axis's own unit wins over the per-product default. Ratio axes are never scaled.
function volumeAxisScale(yAxisTitle: Maybe<string>): Maybe<number> {
  if (!yAxisTitle) return undefined;
  const unit = getYAxisUnit(yAxisTitle).numerator.toLowerCase();
  return VOLUME_UNITS.has(unit) ? scaleDataBasedOnYAxisUnits(yAxisTitle) : undefined;
}

export function getForecastSeries(
  product: string,
  x,
  y: number,
  chartType: ChartTypeLabels,
  rateCum: RateDto[], // this is forecast data, not regular cum
  isBackfitSeries: boolean,
  yAxisTitle?: Maybe<string>
) {
  const isGas = product.toUpperCase() === "GAS";
  const axisScale = volumeAxisScale(yAxisTitle);
  // Total Rate Cum plots the same cum-x/rate-y series as Rate Cum, in the same units.
  if (chartType == "Rate Cum" || chartType == "Total Rate Cum") {
    return rateCum.map((rc) => {
      return [rc.cum * bblMcfToMbblMmcf + x, rc.rate, undefined];
    });
  }
  const totalLength = rateCum.length;
  if (chartType == "Rate Time") {
    return rateCum
      .map((rc, i) => {
        const month = isBackfitSeries ? x - totalLength + i : i + 1 + x;
        if (month <= 0) {
          return undefined;
        }
        return [month, rc.rate, undefined];
      })
      .filter((item) => !!item);
  }
  if (chartType == "Cum Time") {
    return rateCum
      .map((rc, i) => {
        //backfit months starts at 0 going forward to x and forecast starts at x going forward
        const month = isBackfitSeries ? x - totalLength + i : i + 1 + x;
        if (month <= 0) {
          return undefined;
        }
        //cum time gas is in bcf instead of mmcf
        const cum = rc.cum * (axisScale ?? (isGas ? McfToBcf : bblMcfToMbblMmcf));
        return [month, cum + y, undefined];
      })
      .filter((item) => !!item);
  }
  if (chartType == "Rate Date") {
    return rateCum.map((rc) => {
      const date = new Date(rc.year, rc.month - 1, rc.day);
      //gas is in MMcf/d instead of Mcf/d
      const rate = rc.rate * (axisScale ?? (isGas ? 0.001 : 1));
      return [date.toJSON(), rate, undefined];
    });
  }
}

export function addForecastSeries(
  ser: OptionSeries,
  preset: IChartPreset,
  series: ProductRates,
  productType: IProductType,
  isBackfitSeries: boolean,
  yAxisTitle?: Maybe<string>,
  forecastSource?: Maybe<string>
) {
  //added indexOf check here because "Total Fluid" key does not match product name
  //from series.product (Total)
  if (
    series.product !== productType.key &&
    series.product !== productType.key.replace(" ", "") //handle WaterCut compare with 'Water Cut'
  ) {
    return;
  }
  // Custom forecast colours will need to be applied here if required in future
  // see getCurrentSeriesStyleByProductLabel usage
  const forecastSeries = getForecastSeries(
    series.product,
    ser.data[ser.data.length - 1][0],
    ser.data[ser.data.length - 1][1],
    getChartTypeAlias(preset.chartType) as ChartTypeLabels,
    series.rate_cum,
    isBackfitSeries,
    yAxisTitle
  );
  return {
    name: forecastSource
      ? `${ser.name} Forecast (${forecastSource})`
      : `${ser.name} Forecast`,
    type: "line",
    showSymbol: false,
    xAxisIndex: ser.xAxisIndex,
    yAxisIndex: ser.yAxisIndex,
    itemStyle: {
      type: "dashed",
      color: ser.itemStyle.color ?? "#000000",
      width: ser.itemStyle.width ?? 2
    },
    lineStyle: {
      type: "dashed",
      color: ser.lineStyle.color ?? "#000000",
      originalColor: ser.lineStyle.originalColor ?? "#000000",
      width: ser.lineStyle.width ?? 2,
      originalWidth: ser.lineStyle.originalWidth ?? 2
    },
    // Forecast series should start from the end of production series
    data: forecastSeries
  };
}
